import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { PlayCircle, ArrowLeft, Plus, Music2, Search, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useRadioPlayer } from "../context/RadioPlayerContext";
import { TAPIN_RADIO_LOGO } from "../lib/radioBrand";
import SponsorCrawl from "../components/SponsorCrawl";
import TipDjButton from "../components/TipDjButton";
import StationLiveChat from "../components/StationLiveChat";
import StationRequestForm from "../components/StationRequestForm";
import SpotifyNowPlayingTile from "../components/SpotifyNowPlayingTile";
import RadioListenerActions from "../components/RadioListenerActions";
import RadioCallerQueuePanel from "../components/RadioCallerQueuePanel";

/**
 * RadioStation — full track list for one station. Creators + admins can
 * TYPE an artist or song title (auto-complete via iTunes Search), pick a
 * result, and the track is added with artwork + metadata prefilled. A
 * "Paste link instead" toggle still lets them drop a YouTube/Spotify URL.
 * Clicking any track fires the global RadioPlayer queue.
 */
export default function RadioStation() {
  const { slug } = useParams();
  const { user } = useAuth();
  const { playQueue, current } = useRadioPlayer();
  const [station, setStation] = useState(null);
  const [adding, setAdding] = useState(false);
  const canAdd = !!user && ["creator", "premium_creator", "admin"].includes(user.role);

  const load = async () => {
    try {
      const { data } = await api.get(`/radio/stations/${slug}`);
      setStation(data);
    } catch { setStation({ error: true }); }
  };
  useEffect(() => { load(); }, [slug]);

  // Poll the host session so listeners see a live "On Air / Music IN" badge.
  // Cheap GET, refreshes every 15s — enough to surface mode changes without
  // hammering the API.
  const [hostSession, setHostSession] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/radio/host/session/${slug}`);
        if (!cancelled) setHostSession(data);
      } catch { /* ignore */ }
    };
    tick();
    const id = setInterval(tick, 15000);
    return () => { cancelled = true; clearInterval(id); };
  }, [slug]);

  // Listener heartbeat — fires every 20s while the station detail page is
  // open. Backend tallies pings within a 60s window and exposes the count
  // via /api/radio/live-now so station cards show "🔴 47 listening". A
  // stable client_id (per-tab) prevents double-counting on page refresh.
  useEffect(() => {
    if (!hostSession?.is_live) return;
    let cid = sessionStorage.getItem("tapintv_radio_cid");
    if (!cid) {
      cid = `c_${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem("tapintv_radio_cid", cid);
    }
    const ping = () => {
      api.post(`/radio/station/${slug}/heartbeat`, { client_id: cid }).catch(() => {});
    };
    ping();
    const id = setInterval(ping, 20000);
    return () => clearInterval(id);
  }, [slug, hostSession?.is_live]);

  if (!station) return <LoadingBlock />;
  if (station.error) return <div className="p-10 text-center text-white/60">Station not found.</div>;

  const tracks = station.tracks || [];

  return (
    <div data-testid="radio-station-page" className="min-h-screen bg-[#05060a] text-white pb-[160px]">
      {hostSession?.is_live && (
        <div className="max-w-3xl mx-auto px-4">
          <SpotifyNowPlayingTile slug={slug} />
          <StationRequestForm slug={slug} />
          <StationLiveChat slug={slug} hostName={hostSession.host_name} />
        </div>
      )}
      <SponsorCrawl filterStationSlug={slug} className="mt-16" />
      {/* Hero header */}
      <section
        className="relative"
        style={{
          minHeight: 320,
          backgroundImage: `linear-gradient(to bottom, rgba(5,6,10,0.5), rgba(5,6,10,1)), url(${station.cover_url || ""})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-10">
          <Link to="/radio" className="inline-flex items-center gap-1 text-white/70 hover:text-white text-sm mb-4">
            <ArrowLeft className="w-4 h-4" /> All stations
          </Link>
          <div className="flex items-start gap-5 flex-wrap">
            <img
              src={TAPIN_RADIO_LOGO}
              alt="Tap IN Radio"
              data-testid="tapin-radio-station-logo"
              className="w-24 h-24 sm:w-28 sm:h-28 object-contain shrink-0 drop-shadow-[0_4px_24px_rgba(220,38,38,0.4)]"
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <div className="text-red-400 uppercase tracking-[0.3em] text-[11px] font-black">TAP IN RADIO · Station</div>
                {hostSession?.is_live && (
                  <span
                    data-testid="radio-station-on-air"
                    className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-red-100 bg-red-600/80 border border-red-300/40 rounded-full px-2 py-0.5 animate-pulse"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-red-200" /> On Air · {hostSession.host_name || "Host"}
                  </span>
                )}
                {hostSession?.is_live && hostSession.mic_music_mode && (
                  <span
                    data-testid="radio-station-music-in"
                    title="The host is mixing in an external music feed via the mic input — not just the deck rotation."
                    className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-amber-100 bg-amber-500/30 border border-amber-300/50 rounded-full px-2 py-0.5"
                  >
                    Music IN
                  </span>
                )}
                {hostSession?.is_live && hostSession.host_id && (
                  <TipDjButton hostId={hostSession.host_id} hostName={hostSession.host_name} />
                )}
              </div>
              <h1 className="font-display text-5xl sm:text-6xl font-black mb-2">{station.name}</h1>
              <p className="text-white/75 text-base max-w-[640px]">{station.description}</p>
            </div>
          </div>
          <div className="mt-5 flex items-center gap-3 flex-wrap">
            <button
              data-testid="radio-station-play"
              onClick={() => playQueue(tracks, 0, { source: `station:${station.slug}` })}
              disabled={!tracks.length}
              className="inline-flex items-center gap-2 bg-tv-accent disabled:opacity-40 hover:bg-yellow-400 text-black font-display font-black text-lg px-6 py-3 rounded-full"
            >
              <PlayCircle className="w-6 h-6" /> Play Station
            </button>
            {canAdd && (
              <button
                data-testid="radio-station-add"
                onClick={() => setAdding((v) => !v)}
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white border border-white/10 font-bold px-5 py-3 rounded-full"
              >
                <Plus className="w-5 h-5" /> {adding ? "Close" : "Add track"}
              </button>
            )}
            {/* Listener-side actions — Request a song / Tip the host / Call in.
                Renders only when a host is live (component does the gate). */}
            <RadioListenerActions stationSlug={station.slug} liveSession={hostSession?.is_live ? hostSession : null} />
          </div>
        </div>
      </section>

      {/* Host-only caller queue — sits below the hero so the host sees
          the queue without leaving the station page. canAdd ≈ "is host". */}
      {canAdd && hostSession?.is_live && (
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-6">
          <RadioCallerQueuePanel stationSlug={station.slug} />
        </div>
      )}

      <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-6 space-y-3">
        {adding && (
          <AddTrackPanel stationSlug={station.slug} stationName={station.name} onAdded={load} />
        )}

        {/* Published episodes — Tap IN Radio shows archived for replay. */}
        <EpisodesList stationSlug={station.slug} stationCover={station.cover_url} />

        <div data-testid="radio-station-tracks" className="divide-y divide-white/5">
          {tracks.length === 0 && (
            <div className="text-white/50 py-10 text-center">
              <Music2 className="w-8 h-8 mx-auto mb-2 text-white/30" />
              No tracks yet. {canAdd && "Add the first one above."}
            </div>
          )}
          {tracks.map((t, i) => {
            const isCurrent = current?.id === t.id;
            return (
              <button
                key={t.id}
                data-testid={`radio-station-track-${t.id}`}
                onClick={() => playQueue(tracks, i, { source: `station:${station.slug}` })}
                className="w-full flex items-center gap-3 py-2.5 hover:bg-white/5 rounded-lg px-2 text-left transition"
              >
                <div className={`w-7 text-center tabular-nums text-sm font-black ${isCurrent ? "text-tv-accent" : "text-white/40"}`}>{i + 1}</div>
                <div
                  className="shrink-0 w-12 h-12 rounded bg-white/10 overflow-hidden"
                  style={{ backgroundImage: `url(${t.cover_url || station.cover_url || ""})`, backgroundSize: "cover", backgroundPosition: "center" }}
                />
                <div className="flex-1 min-w-0">
                  <div className={`font-bold text-sm line-clamp-1 ${isCurrent ? "text-tv-accent" : ""}`}>{t.title}</div>
                  <div className="text-white/55 text-xs line-clamp-1">{t.artist}{t.album ? ` · ${t.album}` : ""}</div>
                </div>
                <div className="text-white/40 text-xs tabular-nums shrink-0">{fmtDur(t.duration_sec)}</div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/**
 * AddTrackPanel — search-as-you-type add-track flow.
 * Creator just types artist/title; iTunes Search provides 10 matches with
 * artwork + 30s preview + metadata. Click a card to add, or switch to
 * "Paste link" mode to drop a full YouTube / Spotify / direct-URL track.
 */
function AddTrackPanel({ stationSlug, stationName, onAdded }) {
  const [mode, setMode] = useState("search"); // "search" | "link"
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [link, setLink] = useState({ title: "", artist: "", source_url: "", cover_url: "" });
  const debounceRef = useRef(null);

  useEffect(() => {
    if (mode !== "search") return;
    clearTimeout(debounceRef.current);
    if (q.trim().length < 2) { setResults([]); return; }
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/radio/search?q=${encodeURIComponent(q)}`);
        setResults(data || []);
      } catch { setResults([]); }
      finally { setSearching(false); }
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [q, mode]);

  const addFromSearch = async (hit, useFullYoutube = false) => {
    if (!hit) return;
    const source_url = useFullYoutube ? hit.youtube_search_url : (hit.preview_url || hit.youtube_search_url);
    const source_type = useFullYoutube ? "youtube"
      : (hit.preview_url ? "url" : "youtube");
    try {
      await api.post(`/radio/stations/${stationSlug}/tracks`, {
        title: hit.title,
        artist: hit.artist,
        album: hit.album,
        cover_url: hit.cover_url,
        source_url,
        source_type,
        duration_sec: hit.duration_sec || 0,
      });
      toast.success(`Added "${hit.title}" to ${stationName}`, { id: "add-track" });
      onAdded?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add track", { id: "add-track" });
    }
  };

  const submitLink = async (e) => {
    e.preventDefault();
    if (!link.title || !link.artist || !link.source_url) {
      toast.error("Fill title, artist, and paste a link", { id: "add-track" });
      return;
    }
    const detected = /youtube|youtu\.be/.test(link.source_url) ? "youtube"
      : /spotify\.com/.test(link.source_url) ? "spotify" : "url";
    try {
      await api.post(`/radio/stations/${stationSlug}/tracks`, { ...link, source_type: detected });
      toast.success(`Added "${link.title}" to ${stationName}`, { id: "add-track" });
      setLink({ title: "", artist: "", source_url: "", cover_url: "" });
      onAdded?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add track", { id: "add-track" });
    }
  };

  return (
    <div data-testid="radio-add-track-form" className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-3">
      {/* Mode toggle */}
      <div className="flex gap-2 text-xs font-bold">
        <button
          data-testid="radio-add-mode-search"
          onClick={() => setMode("search")}
          className={`px-3 py-1.5 rounded-full ${mode === "search" ? "bg-tv-accent text-black" : "bg-white/10 text-white/70 hover:bg-white/15"}`}
        >
          <Search className="w-3.5 h-3.5 inline mr-1" /> Search catalog
        </button>
        <button
          data-testid="radio-add-mode-link"
          onClick={() => setMode("link")}
          className={`px-3 py-1.5 rounded-full ${mode === "link" ? "bg-tv-accent text-black" : "bg-white/10 text-white/70 hover:bg-white/15"}`}
        >
          <ExternalLink className="w-3.5 h-3.5 inline mr-1" /> Paste link
        </button>
      </div>

      {mode === "search" ? (
        <>
          <div className="relative">
            <input
              data-testid="radio-add-search-input"
              type="text"
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Start typing: artist or song title…"
              className="w-full bg-black/40 border border-white/10 rounded px-10 py-2.5 text-sm text-white focus:border-tv-accent outline-none"
            />
            <Search className="w-4 h-4 text-white/40 absolute left-3.5 top-1/2 -translate-y-1/2" />
            {searching && <Loader2 className="w-4 h-4 text-tv-accent animate-spin absolute right-3 top-1/2 -translate-y-1/2" />}
          </div>

          {results.length > 0 && (
            <div data-testid="radio-add-results" className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[440px] overflow-y-auto pr-1">
              {results.map((hit, i) => (
                <div
                  key={`${hit.itunes_id || i}`}
                  data-testid={`radio-add-result-${i}`}
                  className="flex items-center gap-3 bg-white/5 hover:bg-white/10 rounded-lg p-2 transition"
                >
                  <div
                    className="shrink-0 w-12 h-12 rounded bg-white/10 overflow-hidden"
                    style={{ backgroundImage: `url(${hit.cover_url || ""})`, backgroundSize: "cover", backgroundPosition: "center" }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm line-clamp-1">{hit.title}</div>
                    <div className="text-white/55 text-xs line-clamp-1">{hit.artist}{hit.album ? ` · ${hit.album}` : ""}</div>
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    <button
                      data-testid={`radio-add-pick-preview-${i}`}
                      onClick={() => addFromSearch(hit, false)}
                      className="bg-tv-accent hover:bg-yellow-400 text-black text-[10px] font-black px-2.5 py-1 rounded"
                      title="Add with 30-sec Apple preview"
                    >
                      + 30s Preview
                    </button>
                    <button
                      data-testid={`radio-add-pick-full-${i}`}
                      onClick={() => addFromSearch(hit, true)}
                      className="bg-white/10 hover:bg-white/20 text-white text-[10px] font-black px-2.5 py-1 rounded border border-white/10"
                      title="Add as YouTube search — opens full-length video in player"
                    >
                      + Full Song
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {q && !searching && results.length === 0 && (
            <div className="text-white/50 text-sm text-center py-6">
              No matches. Try a different spelling, or switch to "Paste link".
            </div>
          )}

          {!q && (
            <div className="text-white/40 text-xs px-1">
              Tip: type <span className="text-tv-accent font-bold">artist + song</span> — e.g. "Frank Ocean Pink White" — then pick a result.
            </div>
          )}
        </>
      ) : (
        <form onSubmit={submitLink} className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input
            data-testid="radio-add-title"
            placeholder="Title"
            value={link.title}
            onChange={(e) => setLink({ ...link, title: e.target.value })}
            className="bg-black/40 border border-white/10 rounded px-3 py-2 text-sm text-white focus:border-tv-accent outline-none"
          />
          <input
            data-testid="radio-add-artist"
            placeholder="Artist"
            value={link.artist}
            onChange={(e) => setLink({ ...link, artist: e.target.value })}
            className="bg-black/40 border border-white/10 rounded px-3 py-2 text-sm text-white focus:border-tv-accent outline-none"
          />
          <input
            data-testid="radio-add-link"
            placeholder="YouTube, Spotify or direct MP3 URL"
            value={link.source_url}
            onChange={(e) => setLink({ ...link, source_url: e.target.value })}
            className="md:col-span-2 bg-black/40 border border-white/10 rounded px-3 py-2 text-sm text-white focus:border-tv-accent outline-none"
          />
          <input
            data-testid="radio-add-cover"
            placeholder="Album cover URL (optional)"
            value={link.cover_url}
            onChange={(e) => setLink({ ...link, cover_url: e.target.value })}
            className="md:col-span-2 bg-black/40 border border-white/10 rounded px-3 py-2 text-sm text-white focus:border-tv-accent outline-none"
          />
          <button
            data-testid="radio-add-submit"
            type="submit"
            className="bg-tv-accent text-black font-black px-4 py-2 rounded hover:bg-yellow-400"
          >
            Add to {stationName}
          </button>
        </form>
      )}
    </div>
  );
}

function fmtDur(sec) {
  if (!sec || sec <= 0) return "—";
  const m = Math.floor(sec / 60);
  const s = String(Math.floor(sec % 60)).padStart(2, "0");
  return `${m}:${s}`;
}

function LoadingBlock() {
  return (
    <div className="min-h-screen bg-[#05060a] flex items-center justify-center text-white/40">Loading station…</div>
  );
}

function EpisodesList({ stationSlug, stationCover }) {
  const [items, setItems] = useState([]);
  const [playing, setPlaying] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/radio/stations/${stationSlug}/episodes`);
        if (!cancelled) setItems(data || []);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [stationSlug]);

  const play = async (ep) => {
    if (playing === ep.id) { audioRef.current?.pause(); setPlaying(null); return; }
    setPlaying(ep.id);
    try { await api.post(`/radio/episodes/${ep.id}/play`); } catch {}
    setTimeout(() => audioRef.current?.play?.(), 60);
  };

  if (items.length === 0) return null;
  const current = items.find((e) => e.id === playing);

  return (
    <section data-testid="station-episodes" className="py-3">
      <div className="text-tv-accent uppercase tracking-[0.28em] text-[10px] font-black mb-2">Recorded Shows</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
        {items.map((ep) => (
          <div key={ep.id} id={`ep-${ep.id}`} data-testid={`station-episode-${ep.id}`} className="bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg p-3 flex gap-3">
            <div className="w-16 h-16 rounded bg-white/10 shrink-0" style={{ backgroundImage: `url(${ep.cover_url || stationCover})`, backgroundSize: "cover" }} />
            <div className="flex-1 min-w-0">
              <div className="text-white font-bold text-sm line-clamp-2">{ep.title}</div>
              <div className="text-white/60 text-xs">{ep.host_display_name} · {Math.floor((ep.duration_sec || 0) / 60)}:{String((ep.duration_sec || 0) % 60).padStart(2, "0")}</div>
              <div className="flex gap-2 mt-1.5">
                <button
                  data-testid={`station-episode-play-${ep.id}`}
                  onClick={() => play(ep)}
                  className="bg-tv-accent hover:bg-yellow-400 text-black font-bold text-xs px-3 py-1 rounded"
                >{playing === ep.id ? "Pause" : "Play"}</button>
                {ep.tracklist?.length > 0 && (
                  <span className="text-white/40 text-xs self-center">{ep.tracklist.length} tracks</span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      {current && (
        <audio
          ref={audioRef}
          src={current.audio_url?.startsWith("http") ? current.audio_url : `${process.env.REACT_APP_BACKEND_URL}${current.audio_url}`}
          controls
          autoPlay
          onEnded={() => setPlaying(null)}
          className="mt-3 w-full"
          data-testid="station-episode-player"
        />
      )}
    </section>
  );
}

